import React, { useState, useEffect, useCallback } from 'react';
import { Button, Grid, Modal, Row, Table } from 'react-bootstrap';
import DatePicker from 'react-date-picker';
import { yyyymmdd } from '../libs/date_convert';
import { CSVLink } from 'react-csv';
import { Items, Order } from '../libs/GraphQL.js';
import useGetRetailers from '../hooks/useGetRetailers';
import LoaderButton from '../components/LoaderButton.js';
import Select from '../atoms/forms/Select';

const DownloadProductionCount = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [forecastData, setForecastData] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchDates, setSearchDates] = useState(generateDateRange(new Date()));
  const [showPicker, setShowPicker] = useState(true);
  const [alertModal, setAlertModal] = useState({ mode: false, text: '' });
  const [shelfLife, setShelfLife] = useState({});
  const [retailerIds, setRetailerIds] = useState(null);
  const { retailerList, errors } = useGetRetailers();
  const [selectKey, setSelectKey] = useState(0);

  const showDatePicker = () => {
    setShowPicker((prev) => !prev);
    setIsLoading(false);
    setRetailerIds(null); // Reset retailer ids
    // Force Select component to remount and reset
    setSelectKey((prev) => prev + 1);
  };

  function generateDateRange(value) {
    let endDate = new Date(value.getTime());
    let copiedValue = new Date(value.getTime());
    let searchDates = [];
    endDate.setDate(endDate.getDate() + 7);
    while (copiedValue < endDate) {
      searchDates.push(yyyymmdd(copiedValue, '-'));
      copiedValue.setDate(copiedValue.getDate() + 1);
    }
    return searchDates;
  }

  const getLiveVendItems = useCallback(async () => {
    var shelfLifeObj = {};
    let itemsRaw = await Items();
    try {
      if ('data' in itemsRaw && 'items' in itemsRaw['data']) {
        let items = itemsRaw.data.items.filter(
          (item) => !item.Categories || !item.Categories.includes('Drinks'),
        );
        for (var idx in items) {
          shelfLifeObj[items[idx]['id']] = items[idx]['shelfLife'];
        }
        setShelfLife(shelfLifeObj);
      } else {
        setAlertModal({ mode: true, text: ['Unable to load Vend items'] });
      }
    } catch (e) {
      console.log(e);
      setAlertModal({
        mode: true,
        text: [
          'Unable to load Vend items, Please check your internet and your permissions',
        ],
      });
    }
  }, []);

  const getForecastData = useCallback(async () => {
    setIsLoading(true);
    try {
      let orderState = [];
      for (const searchDate of searchDates) {
        try {
          let order = await Order(null, searchDate, null);
          orderState = orderState.concat(order.data.ordersByDay.orders);
          while (order.data.ordersByDay.nextToken !== null) {
            order = await Order(
              order.data.ordersByDay.nextToken,
              searchDate,
              null,
            );
            orderState = orderState.concat(order.data.ordersByDay.orders);
          }
        } catch (err) {
          setAlertModal({
            mode: true,
            text: [
              `Error fetching orders for date ${searchDate}: ${err.errors
                ?.map((e) => e.message)
                .join(', ')}`,
            ],
          });
          setIsLoading(false);
          return;
        }
      }
      // Deduplicate by id if possible, avoid counting same order twice
      // in case of API returning duplicates
      const seen = new Set();
      orderState = orderState.filter((order) => {
        const key = order.id || JSON.stringify(order);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (retailerIds) {
        orderState = orderState.filter((data) =>
          retailerIds.includes(data.wholesaleLocationId),
        );
      }

      const finalCount = new Map();
      for (const order of orderState) {
        if (!order.itemsCount) continue;
        for (const ic of order.itemsCount) {
          if (
            'productionDate' in ic &&
            searchDates.includes(ic['productionDate'])
          ) {
            const key = `${ic.productionDate}|${ic.itemName}`;
            if (!finalCount.has(key)) {
              finalCount.set(key, {
                count: 0,
                item: ic.itemName,
                prodDate: ic.productionDate,
              });
            }
            finalCount.get(key).count += ic.count;
          }
        }
      }
      // Build forecastDataArr
      const forecastDataArr = [];
      for (const entry of finalCount.values()) {
        let donateByDate = new Date(entry.prodDate);
        donateByDate = new Date(
          donateByDate.getTime() + new Date().getTimezoneOffset() * 60 * 1000,
        );
        donateByDate.setDate(donateByDate.getDate() + 1);
        if (!(entry.item in shelfLife)) {
          entry.donateBy = 'Shelf life for this item is unavailable';
        } else {
          donateByDate.setDate(donateByDate.getDate() + shelfLife[entry.item]);
          entry.donateBy = yyyymmdd(donateByDate, '-');
        }
        forecastDataArr.push(entry);
      }
      setIsLoading(false);
      setForecastData(forecastDataArr);
      setShowPicker(false);
    } catch (e) {
      console.error(e);
    }
  }, [searchDates, shelfLife, retailerIds]);

  const handleDateChange = (value) => {
    setSearchDates(generateDateRange(value));
    setCurrentDate(value);
    setRetailerIds(null); // Reset retailer ids
    // Force Select component to remount and reset
    setSelectKey((prev) => prev + 1);
  };

  const getFileName = () => {
    var startingDate = 'unknown';
    if (searchDates !== undefined && searchDates.length > 0) {
      startingDate = searchDates[0];
    }
    return 'ProductionCount-Starting ' + startingDate + '-' + 'all-regions';
  };

  let headers = [
    <tr key={'header-table'}>
      <th key={'prodDate'}>Production Date</th>
      <th key={'item'}>Item</th>
      <th key={'count'}>Count</th>
      <th key={'donateBy'}>Donate By</th>
    </tr>,
  ];

  const rows = React.useMemo(() => {
    if (forecastData.length > 0) {
      return forecastData
        .slice()
        .sort(function compare(a, b) {
          if (a.prodDate < b.prodDate) {
            return -1;
          }
          if (a.prodDate > b.prodDate) {
            return 1;
          }
          return 0;
        })
        .map((header) => (
          <tr key={header['item'] + header['prodDate']}>
            <td>{header['prodDate']}</td>
            <td>{header['item']}</td>
            <td>{header['count']}</td>
            <td>{header['donateBy']}</td>
          </tr>
        ));
    } else {
      return (
        <tr key={'NO-Data'}>
          <td key={'NO-Data'}> No Data for these dates</td>
        </tr>
      );
    }
  }, [forecastData]);

  useEffect(() => {
    getLiveVendItems();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (errors) {
      // Retailers fetching error handler
      setAlertModal({
        mode: true,
        text: [
          'Unable to load Vend items, Please check your internet and your permissions',
        ],
      });
    }
  }, [errors]);

  return (
    <div>
      {alertModal.mode && (
        <Modal show={alertModal.mode}>
          <Modal.Body>
            <div className="form-group">{alertModal.text}</div>
          </Modal.Body>
          <Modal.Footer>
            &nbsp;
            <Button
              bsStyle="warning"
              onClick={() => setAlertModal({ ...alertModal, mode: false })}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {!showPicker && (
        <LoaderButton
          block
          bsStyle="primary"
          bsSize="large"
          onClick={showDatePicker}
          type="submit"
          isLoading={isLoading}
          text="Choose Another Date or Filter"
          loadingText="Loading Locations…"
        />
      )}
      {showPicker && (
        <Grid style={{ minHeight: '100vh' }}>
          <Row className="text-center" style={{ marginBottom: '15px' }}>
            <h3>Production download count page</h3>
            <h4 style={{ marginBottom: '40px' }}>
              Use this page to download the total count for each product, you
              can filter by retailer if need.
            </h4>
            <div
              style={{
                display: 'flex',
                justifyItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: '20px',
                marginBottom: '20px',
              }}
            >
              <h4 style={{ display: 'inline-block' }}>
                Select an starting date:
              </h4>
              <DatePicker onChange={handleDateChange} value={currentDate} />
            </div>
            <div
              style={{
                display: 'flex',
                justifyItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: '20px',
                marginBottom: '20px',
              }}
            >
              {Object.entries(retailerList).length > 0 ? (
                <>
                  <h4 style={{ display: 'inline-block' }}>
                    Filter by retailer:
                  </h4>
                  <Select
                    key={selectKey}
                    id="retailer-select"
                    options={Object.entries(retailerList).map(
                      ([name, ids]) => ({
                        value: ids, // Use the first ID for the value
                        label: name,
                      }),
                    )}
                    placeholder={'Select a retailer (optional)'}
                    onChange={(v) => {
                      setRetailerIds(v.value);
                    }}
                  />
                </>
              ) : (
                <h4 style={{ display: 'inline-block' }}>
                  Loading retailers...
                </h4>
              )}
            </div>
          </Row>
          <Row className="text-center" style={{ marginBottom: '15px' }}></Row>
          <Row className="text-center">
            <Button
              bsStyle="primary"
              bsSize="large"
              onClick={getForecastData}
              disabled={isLoading}
            >
              {' '}
              {!isLoading ? 'Search' : 'Loading...'}{' '}
            </Button>
          </Row>
        </Grid>
      )}
      {!isLoading && !showPicker && (
        <div>
          <div>
            <Table bordered responsive>
              <thead>{headers}</thead>
              <tbody>{rows}</tbody>
            </Table>
          </div>
          <div>
            <Row className="text-center">
              <h2>
                <CSVLink data={forecastData} filename={getFileName()}>
                  Download as CSV
                </CSVLink>
              </h2>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadProductionCount;
